import { FormControl, FormGroup } from "@angular/forms";
import { Employee } from "../employees/employees.interface";
import type { BrandFontPreset } from "../shared/font-presets.interface";

export type DigitalCardFontPreset = BrandFontPreset;

export interface BrandData {
  name?: string;
  description?: string;
  logos?: Array<{
    type?: string;
    theme?: string;
    formats?: Array<{ format?: string; src?: string }>;
  }>;
  links?: Array<{ name?: string; url?: string }>;
  colors?: Array<{ hex?: string; type?: string }>;
}

export interface CompanyData {
  name?: string;
  website?: string;
  address?: string;
  phone?: string;
  photoUrl?: string;
  brandFontFamily?: string | null;
  brandFontFileUrl?: string | null;
  brandFontFileName?: string | null;
}

export type WalletLogoMode = "inherit" | "hidden" | "custom";

export type WalletGooglePrimaryImage =
  "employee_photo" | "company_logo" | "none";

export type WalletGoogleRightColumn = "auto" | "phone" | "job" | "empty";

export const DEFAULT_WALLET_QR_CAPTION = "Scan to Add Contact";

export interface WalletSettingsData {
  inheritWalletBackgroundFromCard?: boolean;
  inheritCardFieldVisibility?: boolean;
  showAvatar?: boolean;
  showJobTitle?: boolean;
  showContactList?: boolean;
  showQr?: boolean;
  showWalletPassCompanyName?: boolean;
  visibleFields?: Record<string, boolean> | null;
  walletQrCaption?: string | null;
  logoMode?: WalletLogoMode;
  logoCustomUrl?: string | null;
  appleBackgroundHex?: string | null;
  appleForegroundRgb?: string | null;
  googleBackgroundHex?: string | null;
  googlePrimaryImage?: WalletGooglePrimaryImage;
  googleRightColumn?: WalletGoogleRightColumn;
}

export interface DigitalCardSettingsData {
  bannerType?: "gradient" | "image";
  bannerGradientStart?: string | null;
  bannerGradientEnd?: string | null;
  bannerImageUrl?: string | null;
  bannerPosterUrl?: string | null;
  bannerIsAnimated?: boolean;
  brandColorPrimary?: string | null;
  brandColorSecondary?: string | null;
  cardBackgroundColor?: string | null;
  pageBackgroundColor?: string | null;
  showBanner?: boolean;
  showAvatar?: boolean;
  showJobTitle?: boolean;
  showQr?: boolean;
  showCompanyLogo?: boolean;
  showAboutMe?: boolean;
  showPronounceName?: boolean;
  showContactList?: boolean;
  showCustomLinks?: boolean;
  showSaveContact?: boolean;
  showBookMeeting?: boolean;
  customHtml?: string | null;
  companyLogoUrl?: string | null;
  fontName?: string | null;
  fontFamily?: string | null;
  fontCssUrl?: string | null;
  vcardDescription?: string | null;
  visibleFields?: Record<string, boolean>;
  fieldOrder?: string[];
  profileLanguage?: string | null;
  [key: string]: unknown;
}

export interface CardFieldToggle {
  key: string;
  label: string;
}

export const BUILT_IN_CARD_FIELD_KEYS = [
  "phoneNumber",
  "mobileNumber",
  "email",
] as const;

export type BuiltInCardFieldKey = (typeof BUILT_IN_CARD_FIELD_KEYS)[number];

export interface EmployeeWithBrand extends Employee {
  brandData?: BrandData | null;
  companyData?: CompanyData | null;
  variableLabels?: Record<string, string>;
  variableIcons?: Record<string, string>;
  digitalCardSettings?: DigitalCardSettingsData | null;
}

export const CARD_PREVIEW_MESSAGE_TYPE = "CARD_PREVIEW_SETTINGS";
export const CARD_PREVIEW_READY_MESSAGE_TYPE = "CARD_PREVIEW_READY";

export interface CardPreviewSnapshot {
  settings: DigitalCardSettingsData;
  walletSettings: WalletSettingsData;
  employee: EmployeeWithBrand | null;
}

export interface WalletDraftFormControls {
  inheritWalletBackgroundFromCard: FormControl<boolean>;
  logoMode: FormControl<WalletLogoMode>;
  logoCustomUrl: FormControl<string>;
  appleBackgroundHex: FormControl<string>;
  googleBackgroundHex: FormControl<string>;
  inheritCardFieldVisibility: FormControl<boolean>;
  walletShowAvatar: FormControl<boolean>;
  walletShowJobTitle: FormControl<boolean>;
  walletShowEmail: FormControl<boolean>;
  walletShowPhone: FormControl<boolean>;
  walletShowPassCompanyName: FormControl<boolean>;
  walletQrCaption: FormControl<string>;
  googlePrimaryImage: FormControl<WalletGooglePrimaryImage>;
  googleRightColumn: FormControl<WalletGoogleRightColumn>;
}

export interface DigitalCardFormControls {
  displayName: FormControl<string>;
  fontPreset: FormControl<string>;
  bannerType: FormControl<"gradient" | "image">;
  bannerGradientStart: FormControl<string>;
  bannerGradientEnd: FormControl<string>;
  brandColorPrimary: FormControl<string>;
  brandColorSecondary: FormControl<string>;
  cardBackgroundColor: FormControl<string>;
  pageBackgroundColor: FormControl<string>;
  showBanner: FormControl<boolean>;
  showAvatar: FormControl<boolean>;
  showJobTitle: FormControl<boolean>;
  showQr: FormControl<boolean>;
  showCompanyLogo: FormControl<boolean>;
  showAboutMe: FormControl<boolean>;
  showPronounceName: FormControl<boolean>;
  showSaveContact: FormControl<boolean>;
  showBookMeeting: FormControl<boolean>;
  visibleFields: FormControl<Record<string, boolean>>;
  fieldOrder: FormControl<string[]>;
  companyLogoUrl: FormControl<string>;
  customHtml: FormControl<string>;
  profileLanguage: FormControl<string>;
  walletDraft: FormGroup<WalletDraftFormControls>;
}

export type PreviewEmployeePayload = Pick<
  EmployeeWithBrand,
  | "fullName"
  | "jobTitle"
  | "userAbout"
  | "email"
  | "gender"
  | "countryOfOrigin"
  | "phoneNumber"
  | "mobileNumber"
  | "calendarLink"
  | "picture"
  | "thumbnailPhotoUrl"
  | "customLinks"
  | "dynamicVariables"
  | "variableLabels"
  | "variableIcons"
  | "companyData"
  | "brandData"
>;
